import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  List,
  ListItem,
  Paper,
  Box,
  CircularProgress,
  Stack,
  Badge,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Slide,
  Drawer,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Fade,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
  Zoom,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import FlightIcon from "@mui/icons-material/Flight";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";

import {
  closeNotificationModal,
  markNotificationReadRequest,
  fetchNotificationsRequest,
} from "../../../redux/notifications/actions";
import { NOTIFICATIONS } from "../Routes/AppRoutes";

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

function FadeTransition(props) {
  return <Fade {...props} timeout={300} />;
}

const NotificationModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isModalOpen, notifications, isLoading, totalunreadcount } =
    useSelector((state) => state.notification);
  const [displayedNotifications, setDisplayedNotifications] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [detailView, setDetailView] = useState("overlay");
  useEffect(() => {
    if (notifications) {
      const sortedNotifications = [...notifications].sort((a, b) => {
        const aIsRead = a.is_readed || a.is_read;
        const bIsRead = b.is_readed || b.is_read;

        if (!aIsRead && bIsRead) return -1;
        if (aIsRead && !bIsRead) return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setDisplayedNotifications(sortedNotifications);
    }
  }, [notifications]);

  useEffect(() => {
    setDetailView(isMobile ? "drawer" : "overlay");
  }, [isMobile]);

  const handleClose = () => {
    dispatch(closeNotificationModal());
  };

  const handleViewAll = () => {
    dispatch(closeNotificationModal());

    dispatch(
      fetchNotificationsRequest({
        pageNo: 1,
        pageSize: 10,
        paginationRequired: true,
      })
    );

    navigate(NOTIFICATIONS);
  };

  const handleViewNotification = (notification) => {
    setSelectedNotification(notification);
    setIsDetailDrawerOpen(true);

    const notificationId =
      notification.id || notification.admin_notification_id;

    if (!notification.is_readed && !notification.is_read) {
      dispatch(markNotificationReadRequest(notificationId));

      setToastMessage("Notification marked as read");
      setShowToast(true);

      setDisplayedNotifications((prevNotifications) => {
        const updatedNotifications = prevNotifications.map((item) =>
          item.id === notification.id ||
          item.admin_notification_id === notification.admin_notification_id
            ? { ...item, is_readed: true }
            : item
        );
        return updatedNotifications.sort((a, b) => {
          const aIsRead = a.is_readed || a.is_read;
          const bIsRead = b.is_readed || b.is_read;

          if (!aIsRead && bIsRead) return -1;
          if (aIsRead && !bIsRead) return 1;

          return new Date(b.created_at) - new Date(a.created_at);
        });
      });
    }
  };
  const handleViewAirports = () => {
    dispatch(closeNotificationModal());
    navigate("/airports");
  };
  const isAirportUpdateNotification = (message) => {
    return (
      message &&
      message.toLowerCase().includes("new airport update pending approval")
    );
  };
  const handleCloseDetailDrawer = () => {
    setIsDetailDrawerOpen(false);

    setTimeout(() => {
      setSelectedNotification(null);
    }, 300);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setShowToast(false);
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return dateString;
    }
  };

  const extractBookingReference = (message) => {
    const match = message?.match(/booking reference number: ([A-Z0-9]+)/);
    return match ? match[1] : "";
  };

  const extractServiceType = (message) => {
    const match = message?.match(/Your ([^-]+) service/);
    return match ? match[1].trim() : "Airport Service";
  };

  const extractAirport = (message) => {
    const match = message?.match(/at :([^\.]+)\./);
    return match ? match[1].trim() : "";
  };

  const extractCustomerName = (message) => {
    const match = message?.match(/Dear ([^,]+),/);
    return match ? match[1].trim() : "";
  };

  const extractDate = (message) => {
    const match = message?.match(/scheduled for ([^\.]+)\./);
    return match ? match[1].trim() : "";
  };

  const getServiceIcon = (serviceType) => {
    if (serviceType?.toLowerCase().includes("airport")) {
      return <FlightIcon sx={{ color: "#ed6c02" }} />;
    } else if (serviceType?.toLowerCase().includes("shuttle")) {
      return <AirportShuttleIcon sx={{ color: "#ed6c02" }} />;
    }
    return <AirportShuttleIcon sx={{ color: "#ed6c02" }} />;
  };

  const getServiceColor = (serviceType) => {
    if (serviceType?.toLowerCase().includes("airport")) {
      return "#f0f7ff";
    } else if (serviceType?.toLowerCase().includes("shuttle")) {
      return "#fff9f0";
    }
    return "#f0f9ff";
  };

  const getAvatarColor = (isRead) => {
    return isRead ? "#f0f0f0" : "#ffead1";
  };
  const getAvatarTextColor = (isRead) => {
    return isRead ? "#909090" : "#ed6c02";
  };
  const renderNotificationDetailOverlay = () => {
    if (!selectedNotification) return null;

    const bookingRef = extractBookingReference(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const serviceType = extractServiceType(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const airport = extractAirport(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const customerName = extractCustomerName(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const date = extractDate(
      selectedNotification.booking_message || selectedNotification.message || ""
    );

    return (
      <Dialog
        open={isDetailDrawerOpen}
        onClose={handleCloseDetailDrawer}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            backgroundColor: getServiceColor(serviceType),
            pt: 3,
            pb: 4,
            px: 3,
          }}
        >
          <IconButton
            onClick={handleCloseDetailDrawer}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(255,255,255,0.9)",
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: "#ed6c02",
                color: "white",
                width: 48,
                height: 48,
                mr: 2,
              }}
            >
              {getServiceIcon(serviceType)}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="600">
                {serviceType}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(selectedNotification.created_at)}
              </Typography>
            </Box>
          </Box>

          {bookingRef && (
            <Chip
              label={`Ref: ${bookingRef}`}
              color="primary"
              variant="outlined"
              icon={<BookmarkIcon />}
              sx={{ mr: 1, mb: 1, borderColor: "#ed6c02", color: "#ed6c02" }}
            />
          )}

          {(selectedNotification.is_readed || selectedNotification.is_read) && (
            <Chip
              label="Read"
              color="success"
              variant="outlined"
              size="small"
              icon={<CheckCircleOutlineIcon />}
              sx={{ ml: 1, mb: 1 }}
            />
          )}
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <EmailIcon sx={{ mr: 1, color: "#ed6c02" }} />
                Message Details
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  border: "1px solid #f0f0f0",
                  whiteSpace: "pre-line",
                  height: "100%",
                  minHeight: "250px",
                }}
              >
                <Typography variant="body1">
                  {selectedNotification.booking_message ||
                    selectedNotification.message}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card
                elevation={0}
                sx={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <CardHeader
                  title="Booking Information"
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                />
                <CardContent>
                  <List disablePadding>
                    {customerName && (
                      <ListItem
                        disablePadding
                        sx={{
                          py: 1.5,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <PersonIcon sx={{ mr: 2, color: "text.secondary" }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Customer
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {customerName}
                          </Typography>
                        </Box>
                      </ListItem>
                    )}

                    {airport && (
                      <ListItem
                        disablePadding
                        sx={{
                          py: 1.5,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <LocationOnIcon
                          sx={{ mr: 2, color: "text.secondary" }}
                        />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Location
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {airport}
                          </Typography>
                        </Box>
                      </ListItem>
                    )}

                    {date && (
                      <ListItem
                        disablePadding
                        sx={{
                          py: 1.5,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <EventIcon sx={{ mr: 2, color: "text.secondary" }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Scheduled Date
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {date}
                          </Typography>
                        </Box>
                      </ListItem>
                    )}

                    <ListItem
                      disablePadding
                      sx={{
                        py: 1.5,
                      }}
                    >
                      <AccessTimeIcon sx={{ mr: 2, color: "text.secondary" }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Notification Received
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {formatDate(selectedNotification.created_at)}
                        </Typography>
                      </Box>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCloseDetailDrawer}
                  sx={{
                    bgcolor: "#ed6c02",
                    "&:hover": { bgcolor: "#c65602" },
                  }}
                >
                  Close
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  const renderNotificationDetailDrawer = () => {
    if (!selectedNotification) return null;

    const bookingRef = extractBookingReference(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const serviceType = extractServiceType(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const airport = extractAirport(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const customerName = extractCustomerName(
      selectedNotification.booking_message || selectedNotification.message || ""
    );
    const date = extractDate(
      selectedNotification.booking_message || selectedNotification.message || ""
    );

    return (
      <Drawer
        anchor="right"
        open={isDetailDrawerOpen}
        onClose={handleCloseDetailDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "550px" },
            p: 0,
            boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            backgroundColor: getServiceColor(serviceType),
            pt: 3,
            pb: 4,
            px: 3,
          }}
        >
          <IconButton
            onClick={handleCloseDetailDrawer}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(255,255,255,0.9)",
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: "#ed6c02",
                color: "white",
                width: 48,
                height: 48,
                mr: 2,
              }}
            >
              {getServiceIcon(serviceType)}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="600">
                {serviceType}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(selectedNotification.created_at)}
              </Typography>
            </Box>
          </Box>

          {bookingRef && (
            <Chip
              label={`Ref: ${bookingRef}`}
              color="primary"
              variant="outlined"
              icon={<BookmarkIcon />}
              sx={{ mr: 1, mb: 1, borderColor: "#ed6c02", color: "#ed6c02" }}
            />
          )}

          {(selectedNotification.is_readed || selectedNotification.is_read) && (
            <Chip
              label="Read"
              color="success"
              variant="outlined"
              size="small"
              icon={<CheckCircleOutlineIcon />}
              sx={{ ml: 1, mb: 1 }}
            />
          )}
        </Box>

        <Box sx={{ px: 3, py: 3 }}>
          <Card
            elevation={0}
            sx={{
              mb: 3,
              border: "1px solid #f0f0f0",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <CardHeader
              title="Booking Information"
              sx={{
                backgroundColor: "#f9f9f9",
                borderBottom: "1px solid #f0f0f0",
              }}
            />
            <CardContent>
              <List disablePadding>
                {customerName && (
                  <ListItem
                    disablePadding
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <PersonIcon sx={{ mr: 2, color: "text.secondary" }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Customer
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {customerName}
                      </Typography>
                    </Box>
                  </ListItem>
                )}

                {airport && (
                  <ListItem
                    disablePadding
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <LocationOnIcon sx={{ mr: 2, color: "text.secondary" }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {airport}
                      </Typography>
                    </Box>
                  </ListItem>
                )}

                {date && (
                  <ListItem
                    disablePadding
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <EventIcon sx={{ mr: 2, color: "text.secondary" }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Scheduled Date
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {date}
                      </Typography>
                    </Box>
                  </ListItem>
                )}

                <ListItem
                  disablePadding
                  sx={{
                    py: 1.5,
                  }}
                >
                  <AccessTimeIcon sx={{ mr: 2, color: "text.secondary" }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Notification Received
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {formatDate(selectedNotification.created_at)}
                    </Typography>
                  </Box>
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Typography
            variant="h6"
            sx={{ mb: 2, display: "flex", alignItems: "center" }}
          >
            <EmailIcon sx={{ mr: 1, color: "#ed6c02" }} /> Message Details
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: "#f9f9f9",
              borderRadius: 2,
              border: "1px solid #f0f0f0",
              whiteSpace: "pre-line",
            }}
          >
            <Typography variant="body1">
              {selectedNotification.booking_message ||
                selectedNotification.message}
            </Typography>
          </Paper>
        </Box>
      </Drawer>
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isModalOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "450px", md: "500px" },
            borderRadius: { xs: 0, sm: "12px 0 0 12px" },
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
            p: 2,
            position: "sticky",
            top: 0,
            bgcolor: "white",
            zIndex: 10,
          }}
        >
          <Box display="flex" alignItems="center">
            <Badge
              badgeContent={totalunreadcount}
              color="error"
              sx={{ mr: 1.5 }}
            >
              <NotificationsNoneIcon sx={{ color: "#ed6c02" }} />
            </Badge>
            <Typography variant="h6" fontWeight="600">
              Notifications
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflow: "auto", height: "calc(100% - 130px)" }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress sx={{ color: "#ed6c02" }} />
            </Box>
          ) : displayedNotifications && displayedNotifications.length > 0 ? (
            <List disablePadding>
              {displayedNotifications.map((notification, index) => {
                const bookingRef = extractBookingReference(
                  notification.booking_message || notification.message || ""
                );
                const serviceType = extractServiceType(
                  notification.booking_message || notification.message || ""
                );
                const airport = extractAirport(
                  notification.booking_message || notification.message || ""
                );
                const customerName = extractCustomerName(
                  notification.booking_message || notification.message || ""
                );
                const isRead = notification.is_readed || notification.is_read;

                return (
                  <FadeTransition
                    key={notification.id || notification.admin_notification_id}
                    in={true}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        mx: 2,
                        my: 1,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: isRead ? "#f0f0f0" : "#ffead1",
                        backgroundColor: isRead ? "white" : "#fffaf5",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                          borderColor: isRead ? "#e0e0e0" : "#ed6c02",
                        },
                      }}
                    >
                      <ListItem
                        sx={{
                          p: 0,
                          cursor: "pointer",
                          display: "block",
                        }}
                        onClick={() => handleViewNotification(notification)}
                      >
                        <Box
                          sx={{
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: "1px solid",
                            borderColor: isRead ? "#f5f5f5" : "#ffead1",
                          }}
                        >
                          <Box display="flex" alignItems="center">
                            <Avatar
                              sx={{
                                bgcolor: getAvatarColor(isRead),
                                color: getAvatarTextColor(isRead),
                                width: 40,
                                height: 40,
                                mr: 1.5,
                              }}
                            >
                              {getServiceIcon(serviceType)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="600">
                                {serviceType}
                                {!isRead && (
                                  <FiberManualRecordIcon
                                    sx={{
                                      ml: 1,
                                      fontSize: 10,
                                      color: "#ed6c02",
                                    }}
                                  />
                                )}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(notification.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                          {bookingRef && (
                            <Chip
                              label={bookingRef}
                              size="small"
                              sx={{
                                bgcolor: isRead ? "#f5f5f5" : "#ffead1",
                                color: isRead ? "text.secondary" : "#ed6c02",
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>

                        <Box sx={{ p: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {notification.booking_message ||
                              notification.message}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            {airport && (
                              <Chip
                                icon={
                                  <LocationOnIcon sx={{ fontSize: "0.9rem" }} />
                                }
                                label={airport}
                                size="small"
                                sx={{ bgcolor: "#f9f9f9" }}
                              />
                            )}

                            {customerName && (
                              <Chip
                                icon={
                                  <PersonIcon sx={{ fontSize: "0.9rem" }} />
                                }
                                label={customerName}
                                size="small"
                                sx={{ bgcolor: "#f9f9f9" }}
                              />
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                            }}
                          >
                            {isAirportUpdateNotification(
                              notification.booking_message ||
                                notification.message
                            ) ? (
                              <Button
                                variant={isRead ? "outlined" : "contained"}
                                size="small"
                                startIcon={<FlightIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewAirports();
                                }}
                                sx={{
                                  minWidth: "120px",
                                  bgcolor: isRead ? "transparent" : "#ed6c02",
                                  borderColor: isRead
                                    ? "#ed6c02"
                                    : "transparent",
                                  color: isRead ? "#ed6c02" : "white",
                                  "&:hover": {
                                    bgcolor: isRead
                                      ? "rgba(237, 108, 2, 0.08)"
                                      : "#c65602",
                                    borderColor: isRead
                                      ? "#ed6c02"
                                      : "transparent",
                                  },
                                }}
                              >
                                View Airports
                              </Button>
                            ) : (
                              <Button
                                variant={isRead ? "outlined" : "contained"}
                                size="small"
                                startIcon={<VisibilityIcon />}
                                sx={{
                                  minWidth: "100px",
                                  bgcolor: isRead ? "transparent" : "#ed6c02",
                                  borderColor: isRead
                                    ? "#ed6c02"
                                    : "transparent",
                                  color: isRead ? "#ed6c02" : "white",
                                  "&:hover": {
                                    bgcolor: isRead
                                      ? "rgba(237, 108, 2, 0.08)"
                                      : "#c65602",
                                    borderColor: isRead
                                      ? "#ed6c02"
                                      : "transparent",
                                  },
                                }}
                              >
                                View
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    </Paper>
                  </FadeTransition>
                );
              })}
            </List>
          ) : (
            <Box py={8} textAlign="center">
              <NotificationsNoneIcon
                sx={{ fontSize: 48, color: "#ed6c02", opacity: 0.4, mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            justifyContent: "space-between",
            position: "sticky",
            bottom: 0,
            bgcolor: "white",
            zIndex: 10,
          }}
        >
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
          <Button
            onClick={handleViewAll}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: "#ed6c02",
              "&:hover": {
                bgcolor: "#c65602",
              },
            }}
          >
            View All Notifications
          </Button>
        </Box>
      </Drawer>

      {detailView === "overlay"
        ? renderNotificationDetailOverlay()
        : renderNotificationDetailDrawer()}
      <Snackbar
        open={showToast}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="success"
          variant="filled"
          icon={<CheckCircleOutlineIcon />}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationModal;
